import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      localStorage.setItem("roamies_admin_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink noise-bg flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-cream p-8 sm:p-10">
        <Link to="/" className="font-display text-2xl tracking-tight text-ink" data-testid="admin-login-logo">
          ROAMIES<span className="text-flame">.</span>
        </Link>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-ink/50 mt-2 mb-8">Admin Panel</p>
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-widest">Email</Label>
            <Input id="admin-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              data-testid="admin-email-input"
              className="rounded-none h-12 border-ink/20 focus-visible:ring-flame" placeholder="you@example.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-widest">Password</Label>
            <div className="relative">
              <Input id="admin-password" type={showPw ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                data-testid="admin-password-input"
                className="rounded-none h-12 border-ink/20 focus-visible:ring-flame pr-12" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} data-testid="admin-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-flame transition-colors">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && (
            <p className="text-sm font-semibold text-red-600" data-testid="admin-login-error">{error}</p>
          )}
          <Button type="submit" disabled={loading} data-testid="admin-login-submit-btn"
            className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        <Link to="/" className="block mt-6 text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-flame transition-colors" data-testid="admin-login-back-link">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
